import React, { ReactNode } from 'react';
import IconButton from '@mui/material/IconButton';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import NumberUtils from '../../utils/numberUtils';
import TwSelect, { TwSelectOption } from '../TwSelects/TwSelects';
import TwFileVisibilitySelect from '../TwFileVisibilitySelect/TwFileVisibilitySelect';
import Stack from '@mui/material/Stack';
import { ImportedLoadAttachmentData, LoadDocument } from '../../redux/api/load/load.model';
import styled from '@mui/material/styles/styled';
import './TwFile.scss';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import { AttachmentBasicProps, OrderAttachment } from '../../redux/api/orders/orders.model';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf-file.svg';

export interface TwFileProps {
  /**
   * Label text for the Field.
   */
  label?: string;
  /**
   * Id for the Field.
   */
  id?: string;
  /**
   * className for the container.
   */
  className?: string;
  /**
   * Field name
   */
  name: string;
  /**
   * File
   */
  file: File | ImportedLoadAttachmentData | AttachmentBasicProps;
  /**
   * Field has error flag
   */
  error?: boolean;
  /**
   * Helper text for the errors
   */
  helperText?: string | undefined;
  /**
   * Adorment to appear at start of the input.
   */
  startAdorment?: ReactNode;
  /**
   * Action to trigger when clearing input
   */
  clearInputAction?: () => void;
  /**
   * Options to show on the Select
   */
  optionsSelectList: TwSelectOption[];
  /**
   * Action that handles type selection
   */
  handleSelectChange: (e: any) => void;
  /**
   * Type Select value
   */
  selectValue: string | undefined;
  /**
   * Hides/shows type dropdown
   */
  showVisibilitySelect: boolean;
  /**
   * Hides/shows visibility dropdown
   */
  showTypeSelect: boolean;
  /**
   * Action to handle visibility change
   */
  handleVisibilityChange: (e: any) => void;
  /**
   * Visibility value
   */
  visibilityValue: boolean | undefined;
  /**
   * Include icon based on file type
   */
  includeIconByType?: boolean;
  /**
   * Click function that triggers the preview
   */
  onClick: () => void;
}

const StyledFileContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  border: '1px solid #E2E2EA',
  borderRadius: '4px',
  backgroundColor: 'none',
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 'normal',
  fontSize: '14px',
  lineHeight: '21px',
  marginBottom: '10px',

  '& input': {
    fontFamily: 'Poppins',
    fontSize: '14px',
    backgroundColor: 'white',
    height: '23px'
  },

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E2E2EA'
  },

  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#3959df'
  },

  '& .MuiInputBase-root .MuiOutlinedInput-input.Mui-disabled': {
    borderColor: '#E2E2EA',
    color: 'rgba(0, 0, 0, 1)',
    textFillColor: 'rgba(0, 0, 0, 1)',
    background: 'none'
  },

  '& .MuiSvgIcon-root': {
    width: '0.8em',
    height: '0.8em'
  },

  '& .MuiIconButton-root': {
    padding: '0',
    color: '#7e858e'
  }
});

const TwFile: React.FC<TwFileProps> = ({
  label,
  id,
  className,
  file,
  optionsSelectList,
  clearInputAction,
  handleSelectChange,
  selectValue,
  showTypeSelect,
  showVisibilitySelect,
  handleVisibilityChange,
  visibilityValue,
  includeIconByType = false,
  onClick
}): JSX.Element => {
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    clearInputAction && clearInputAction();
  };

  const handleMouseDownClear = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
  };

  const getFileIcon = (): ReactNode => {
    if (!includeIconByType) return <InsertDriveFileOutlinedIcon className="tw-file__adornment" />;

    if ('type' in file) {
      if (file.type?.match('pdf')) {
        return <PdfIcon />;
      }

      return <InsertDriveFileOutlinedIcon className="tw-file__adornment" />;
    } else {
      return <InsertDriveFileOutlinedIcon className="tw-file__adornment" />;
    }
  };

  return (
    <div className={`${className} tid-file`}>
      {label && (
        <label className="tid-file-label" htmlFor={id}>
          <span className="tw-file__label">{label}</span>
        </label>
      )}

      <StyledFileContainer
        sx={{
          flexDirection: matchesMobileOrTablet ? 'column' : 'row',
          height: matchesMobileOrTablet ? 'max-content' : '100%',
          padding: matchesMobileOrTablet ? '10px 20px 0px 20px' : '8px'
        }}
      >
        <Stack
          alignItems="center"
          flexDirection={'row'}
          onClick={onClick}
          width={matchesMobileOrTablet ? '100%' : '50%'}
        >
          {getFileIcon()}
          <Stack flexDirection={'column'} width="90%">
            <span className={`tw-file__filename tid-file-name`}>{file.name}</span>
            <span className={`tw-file__filesize tid-file-size`}>
              {file.size ? NumberUtils.getFileSize(file.size) : ''}
            </span>
          </Stack>
        </Stack>
        <Stack
          alignItems={'center'}
          flexDirection="row"
          width={matchesMobileOrTablet ? '100%' : '50%'}
          justifyContent={'flex-end'}
        >
          {showTypeSelect && (
            <TwSelect
              id="attachmentType"
              name="attachmentType"
              value={selectValue}
              optionList={optionsSelectList}
              onInputChange={handleSelectChange}
              selectSx={{
                '& .MuiInputBase-input': {
                  backgroundColor: 'transparent !important',
                  color: '#9CA3AF !important'
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
              }}
            />
          )}
          {showVisibilitySelect && (
            <TwFileVisibilitySelect
              name="visibility"
              value={visibilityValue ? 'visible' : 'hidden'}
              onInputChange={handleVisibilityChange}
            />
          )}
          {!!clearInputAction && (
            <IconButton
              aria-label="delete"
              onClick={handleClear}
              onMouseDown={handleMouseDownClear}
            >
              {!!file && <ClearIcon />}
            </IconButton>
          )}
        </Stack>
      </StyledFileContainer>
    </div>
  );
};

export default TwFile;
